import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Pencil, Eye, Trash2 } from "lucide-react";
import { BACKEND_URL } from "../../config";
import Modal from "../../components/Modal";
import ConfirmModal from "../../components/ConfirmModal";
import SidebarLayout from "../../components/SidebarLayout";
import { Medicine } from "./types";
import { useAuth } from "../../modules/auth/authContext";

const normalizeCatalogType = (type?: string) => String(type || "").trim().toUpperCase();
const isServiceType = (type?: string) => ["SERVICE", "SERVICES"].includes(normalizeCatalogType(type));
const isLabType = (type?: string) => ["LAB", "LABS", "TEST", "TESTS"].includes(normalizeCatalogType(type));

type SortKey = "name" | "updatedAt" | "quantity" | "expiryDate" | "manufacturer";
type SortDirection = "asc" | "desc";


const MedicinesList: React.FC = () => {
  const location = useLocation();
  const [items, setItems] = useState<Medicine[]>([]);
  const [loading, setLoading] = useState(true);

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    id?: string;
  }>({ open: false });

  const [filters, setFilters] = useState<any>({});
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const { can } = useAuth();
  const canEditCatalog = can("catalog.edit");
  const canDeleteCatalog = can("catalog.delete");
  const catalogMode = location.pathname.startsWith("/services")
    ? { label: "Service", plural: "Services", category: "SERVICE", basePath: "/services" }
    : location.pathname.startsWith("/labs")
      ? { label: "Lab", plural: "Labs", category: "LAB", basePath: "/labs" }
      : { label: "Medicine", plural: "Medicines", category: "", basePath: "/medicines" };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/medicines`, {
          method: "GET",
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setItems(data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const openDeleteConfirm = (id: string) => {
    setConfirmDelete({ open: true, id });
  };

  const handleDelete = async (id?: string) => {
    setConfirmDelete({ open: false });
    if (!id) return;

    const response = await fetch(`${BACKEND_URL}/api/medicines/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (response.ok) {
      setItems((prev) => prev.filter((x) => x.id !== id));
      setModal({ open: true, title: "Deleted", message: `${catalogMode.label} deleted` });
    }
  };

  const handleFilter = (key: string, value: string) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
  };
  const setColumnSort = (column: SortKey, direction: SortDirection) => {
    setSortKey(column);
    setSortDirection(direction);
  };

  const SortHeader = ({ column, label, align = "left" }: { column: SortKey; label: string; align?: "left" | "right" }) => (
    <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : "justify-start"}`}>
      <span>{label}</span>
      <span className="inline-flex rounded border border-border-light bg-white">
        <button
          type="button"
          onClick={() => setColumnSort(column, "asc")}
          className={`inline-flex h-5 w-5 items-center justify-center rounded-l hover:bg-primary/10 ${
            sortKey === column && sortDirection === "asc" ? "bg-primary/10" : ""
          }`}
          title={`Sort ${label} ascending`}
          aria-label={`Sort ${label} ascending`}
        >
          <span className={`h-0 w-0 border-x-[4px] border-x-transparent border-b-[6px] ${sortKey === column && sortDirection === "asc" ? "border-b-primary" : "border-b-subtext-light"}`} />
        </button>
        <button
          type="button"
          onClick={() => setColumnSort(column, "desc")}
          className={`inline-flex h-5 w-5 items-center justify-center rounded-r border-l border-border-light hover:bg-primary/10 ${
            sortKey === column && sortDirection === "desc" ? "bg-primary/10" : ""
          }`}
          title={`Sort ${label} descending`}
          aria-label={`Sort ${label} descending`}
        >
          <span className={`h-0 w-0 border-x-[4px] border-x-transparent border-t-[6px] ${sortKey === column && sortDirection === "desc" ? "border-t-primary" : "border-t-subtext-light"}`} />
        </button>
      </span>
    </div>
  );


const formatMonthYear = (dateStr: string | undefined) => {
  if (!dateStr) return "-";
  const [, month, year] = dateStr.split("-");
  return `${month}/${year}`;
};

const normalizeExpiry = (value?: string) => {
  if (!value) return "";
  return value.replace(/\//g, "-");
};
const parseDateTime = (value?: string) => {
  if (!value) return Number.POSITIVE_INFINITY;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

const parseExpiryDate = (value?: string) => {
  if (!value) return Number.POSITIVE_INFINITY;
  const [day, month, year] = value.split("-").map(Number);
  if (!day || !month || !year) return Number.POSITIVE_INFINITY;
  return new Date(year, month - 1, day).getTime();
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};


  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (catalogMode.category === "SERVICE" && !isServiceType(item.type)) return false;
      if (catalogMode.category === "LAB" && !isLabType(item.type)) return false;
      if (!catalogMode.category && (isServiceType(item.type) || isLabType(item.type))) return false;

      if (
        filters.name &&
        !item.name?.toLowerCase().includes(filters.name.toLowerCase())
      )
        return false;

      if (
        filters.brand &&
        !item.brand?.toLowerCase().includes(filters.brand.toLowerCase())
      )
        return false;

      if (
        filters.type &&
        !item.type?.toLowerCase().includes(filters.type.toLowerCase())
      )
        return false;

      if (
        filters.manufacturer &&
        !item.manufacturer
          ?.toLowerCase()
          .includes(filters.manufacturer.toLowerCase())
      )
        return false;

      if (
        filters.expiryDate &&
        !item.expiryDate?.includes(normalizeExpiry(filters.expiryDate))
      )
        return false;

      if (
        filters.minQuantity &&
        item.quantity < Number(filters.minQuantity)
      )
        return false;

      if (
        filters.maxPrice &&
        (item.mrpPrice === undefined || item.mrpPrice > Number(filters.maxPrice))
      )
        return false;

      return true;
    });
  }, [catalogMode.category, filters, items]);

  const sortedItems = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
    const directionMultiplier = sortDirection === "desc" ? -1 : 1;

    return [...filteredItems].sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case "updatedAt":
          comparison = parseDateTime(a.updatedAt) - parseDateTime(b.updatedAt);
          break;
        case "quantity":
          comparison = (a.quantity ?? 0) - (b.quantity ?? 0);
          break;
        case "expiryDate":
          comparison = parseExpiryDate(a.expiryDate) - parseExpiryDate(b.expiryDate);
          break;
        case "manufacturer":
          comparison = collator.compare(a.manufacturer || "", b.manufacturer || "");
          break;
        case "name":
        default:
          comparison = collator.compare(a.name || "", b.name || "");
          break;
      }

      return comparison * directionMultiplier || collator.compare(a.name || "", b.name || "");
    });
  }, [filteredItems, sortDirection, sortKey]);

  return (
    <SidebarLayout title={`${catalogMode.plural} Catalog`}>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <Modal
            open={modal.open}
            title={modal.title}
            message={modal.message}
            onClose={() => setModal({ ...modal, open: false })}
          />

          <ConfirmModal
            open={confirmDelete.open}
            message="Delete this medicine?"
            onCancel={() => setConfirmDelete({ open: false })}
            onConfirm={() => handleDelete(confirmDelete.id)}
          />

          {/* Filters */}
          <div className="bg-surface-light p-4 rounded border">
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <input
                className="p-2 border rounded"
                placeholder="Search name"
                value={filters.name || ""}
                onChange={(e) => handleFilter("name", e.target.value)}
              />
              {!catalogMode.category && (
                <>
                  <input
                    className="p-2 border rounded"
                    placeholder="Search Brand"
                    value={filters.brand || ""}
                    onChange={(e) => handleFilter("brand", e.target.value)}
                  />
                  <input
                    className="p-2 border rounded"
                    placeholder="Search medicine type"
                    aria-label="Search medicine type"
                    value={filters.type || ""}
                    onChange={(e) => handleFilter("type", e.target.value)}
                  />
                </>
              )}

              <input
                className="p-2 border rounded"
                placeholder="Search supplier"
                value={filters.manufacturer || ""}
                onChange={(e) =>
                  handleFilter("manufacturer", e.target.value)
                }
              />

              {!catalogMode.category && (
                <>
                  <input
                    className="p-2 border rounded"
                    placeholder="Search expiry (YYYY-MM)"
                    value={filters.expiryDate || ""}
                    onChange={(e) =>
                      handleFilter("expiryDate", e.target.value)
                    }
                  />

                  <input
                    className="p-2 border rounded"
                    placeholder="Min stock"
                    value={filters.minQuantity || ""}
                    onChange={(e) =>
                      handleFilter("minQuantity", e.target.value)
                    }
                  />
                </>
              )}

              <input
                className="p-2 border rounded"
                placeholder="Max price"
                value={filters.maxPrice || ""}
                onChange={(e) =>
                  handleFilter("maxPrice", e.target.value)
                }
              />
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-left" >
                <thead>
                  <tr className="text-xs text-subtext-light">
                    <th className="p-2"></th>
                    <th className="p-2"><SortHeader column="name" label="Name" /></th>
                    {!catalogMode.category && <th className="p-2">Brand</th>}
                    {!catalogMode.category && <th className="p-2 whitespace-nowrap">Medicine Type</th>}
                    {!catalogMode.category && <th className="p-2 whitespace-nowrap">Meal Instruction</th>}
                    {!catalogMode.category && <th className="p-2"><SortHeader column="quantity" label="Stock" /></th>}
                    {!catalogMode.category && <th className="p-2"><SortHeader column="expiryDate" label="Expiry" /></th>}
                    {!catalogMode.category && <th className="p-2"><SortHeader column="updatedAt" label="Last Updated" /></th>}
                    <th className="p-2">Supplier</th>
                    {!catalogMode.category && <th className="p-2">Batch Number</th>}
                    <th className="p-2">MRP Price</th>
                    {false && !catalogMode.category && <th className="p-2">Buying Price</th>}
                    <th className="p-2 sticky right-0 bg-surface-light shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {sortedItems.map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="p-2">
                        <input type="checkbox" />
                      </td>
                      <td className="p-2 font-medium">{item.name}</td>
                      {!catalogMode.category && <td className="p-2 font-medium">{item.brand}</td>}
                      {!catalogMode.category && <td className="p-2">{item.type?.trim() || "Not specified"}</td>}
                      {!catalogMode.category && <td className="p-2">{item.default_meal_instruction === "before_meal" ? "Before meal" : item.default_meal_instruction === "after_meal" ? "After meal" : "No default"}</td>}
                      {!catalogMode.category && <td className="p-2">{item.quantity}</td>}
                      {!catalogMode.category && <td className="p-2">{formatMonthYear(item.expiryDate)}</td>}
                      {!catalogMode.category && <td className="p-2 whitespace-nowrap text-sm">{formatDateTime(item.updatedAt)}</td>}
                      <td className="p-2">{item.manufacturer}</td>
                      {!catalogMode.category && <td className="p-2">{item.batchNo}</td>}
                      <td className="p-2">
                        ₹{item.mrpPrice?.toFixed(2)}
                      </td>
                      {false && !catalogMode.category && <td className="p-2">
                        ₹{item.buyingPrice?.toFixed(2)}
                      </td>}
                      <td className="p-2 sticky right-0 bg-surface-light shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]">
                        <div className="flex flex-nowrap items-center gap-1">
                          <Link
                            to={`${catalogMode.basePath}/new`}
                            state={{ medicine: item, view: true }}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-border-light text-subtle-light hover:bg-primary/10 hover:text-primary"
                            title={`View ${catalogMode.label.toLowerCase()}`}
                            aria-label={`View ${item.name}`}
                          >
                            <Eye size={16} />
                          </Link>

                          {canEditCatalog && (
                            <Link
                              to={`${catalogMode.basePath}/new`}
                              state={{ medicine: item }}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-border-light text-subtle-light hover:bg-primary/10 hover:text-primary"
                              title={`Edit ${catalogMode.label.toLowerCase()}`}
                              aria-label={`Edit ${item.name}`}
                            >
                              <Pencil size={16} />
                            </Link>
                          )}

                          {canDeleteCatalog && (
                            <button
                              onClick={() =>
                                openDeleteConfirm(item.id)
                              }
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-alert-red/30 text-alert-red hover:bg-alert-red/10"
                              title="Delete medicine"
                              aria-label={`Delete ${item.name}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </SidebarLayout>
  );
};

export default MedicinesList;
